<template>
    <div v-if="useCaseMessage" class="usecase">
        {{ useCaseMessage }}
    </div>
</template>

<script setup lang="ts">
    import { TastierClient } from "@/app/TastierClient";
    import { trans } from "@/app/localization/Trans";
    import { FulfillmentHandling } from "@/data/model/Enums";
    import type { IOrderApplication } from "@/data/model/OrderApplication";
    import type { IMultiLanguageText } from "@/data/model/Shared";
    import { Format } from "@/data/util/Format";
    import { clientLanguage } from "@/shared/localization/language";
    import { computed } from "vue";

    const props = defineProps<{
        useCase: IOrderApplication | undefined;
        tableName: string | undefined;
        isAnyFulfillment: boolean;
        isAnyFulfillmentReady: boolean;
        messageToUserAfterOrderPlaced: IMultiLanguageText | undefined;
    }>();

    const useCaseMessage = computed(() => {
        if (props.messageToUserAfterOrderPlaced != null) {
            const translation = Format.translate(props.messageToUserAfterOrderPlaced, clientLanguage());
            if (translation != null && translation.length > 0) {
                return translation;
            }
        }

        // Return empty string if no order placed text is available
        if (!props.useCase?.orderPlacedText) {
            return "";
        }

        // Get the translated order text and replace [tablename] placeholder
        const orderPlacedText = Format.translate(props.useCase.orderPlacedText, clientLanguage()).replace(
            "[tablename]",
            props.tableName ?? ""
        );

        // Handle fulfillment-specific messages
        if (props.isAnyFulfillment) {
            // Show pickup message if fulfillment is ready
            if (props.isAnyFulfillmentReady) {
                return trans.fulfillmentPopover.showWhenPickingUpOrder;
            }

            // Show push notification message if notifications are enabled
            if (TastierClient.context.isPushNotificationsReady(props.useCase)) {
                return TastierClient.context.getOrderPlacedMessageIfNotificationEnabled(props.useCase);
            }

            // Show standard order placed text for fulfillment
            return orderPlacedText;
        }

        // Hide message for operator and app fulfillment handling
        if (props.useCase.fulfillmentHandling === FulfillmentHandling.OperatorAndApp) {
            return "";
        }

        // Default case: return standard order placed text
        return orderPlacedText;
    });
</script>

<style lang="scss">
    .order-received-popover-content .usecase {
        font-size: 14px;
        font-weight: 700;
        text-align: center;
        padding: 12px 0;
        line-height: 22px;
    }
</style>
